import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, BarChart3, Download, Mail, RotateCcw, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { DataTable } from '../../components/ui/data-table';
import { useAuth } from '../../contexts/AuthContext';
import { useApiConfig } from '../../hooks/useApiConfig';
import { BACKEND_BASE_URL, BACKEND_DIRECT_ACCESS_CODE } from '../../config/backendConfig';
import { fetchCampaignLiveMetrics } from '../../utils/api';
import {
  ACTIVATION_LABEL, ALLOCATION_LABEL, ISSUE_ORDER, ISSUE_TYPES,
  buildKamNames, buildLiveCampaigns, buildResponsibleRows, buildSummaryRows,
  deltaDirection, exportCustomerIoJson, exportFounderSummaryExcel,
  exportFounderWoWExcel, exportGenericExcel, exportResponsibleExcel,
  filterCampaignRows, fmtInr, fmtPctStr, matchesActivation, matchesKam,
} from '../../utils/campaignLiveEngine';

// ── Small presentational bits ────────────────────────────────────────────────

function DeltaBadge({ pct, comparable = true, reason }) {
  // A suppressed delta is not the same as a missing one: "not comparable" says
  // the platform mix changed under us, "N/A" says there is simply nothing to
  // compare. Collapsing the two is how a fabricated jump gets read as real.
  if (comparable === false) {
    return (
      <span className="wow-na" title={reason || 'Not comparable with last week.'}>
        not comparable
      </span>
    );
  }
  const dir = deltaDirection(pct);
  if (dir === 'na') return <span className="wow-na">N/A</span>;
  return <span className={`wow-badge wow-${dir}`}>{fmtPctStr(pct)}</span>;
}

function AllocationPill({ status }) {
  const cfg = ALLOCATION_LABEL[status] ?? ALLOCATION_LABEL.NONE;
  return <span className={`pill pill-${cfg.tone}`}>{cfg.label}</span>;
}

function ActivationPill({ status }) {
  const cfg = ACTIVATION_LABEL[status];
  if (!cfg) return <span className="contact-missing">—</span>;
  return <span className={`pill pill-${cfg.tone}`}>{cfg.label}</span>;
}

function ClientCell({ clientId, clientName, activationStatus, kamName, error }) {
  return (
    <span className="client-name-cell">
      <span className="client-name">
        {clientName}
        {activationStatus === 'TRIAL' && <span className="new-plat-tag trial-tag">TRIAL</span>}
      </span>
      <span className="client-id">#{clientId}{kamName ? ` · ${kamName}` : ''}</span>
      {error && (
        <span className="contact-missing" title={error}>
          <AlertTriangle size={12} /> failed to load
        </span>
      )}
    </span>
  );
}

function MoneyCell({ value, sub }) {
  if (!value) return <div className="num-cell"><div className="num-primary">—</div></div>;
  return (
    <div className="num-cell">
      <div className="num-primary">{fmtInr(value)}</div>
      {sub ? <div className="num-sub">{sub}</div> : null}
    </div>
  );
}

const ACTIVATION_OPTIONS = ['ACTIVE', 'TRIAL'];

// One metric contributes two columns — the live rupee value and its movement —
// so each can be sorted, range-filtered and hidden independently.
function metricColumns(key, label) {
  return [
    {
      id: `${key}Value`,
      accessorFn: r => r[key]?.currentValue ?? 0,
      header: label,
      filterFn: 'numberRange',
      meta: { label: `${label} value`, filterVariant: 'numberRange' },
      cell: ({ row }) => (
        <MoneyCell
          value={row.original[key]?.currentValue}
          sub={row.original[key]?.currentCount ? `${row.original[key].currentCount} inv` : null}
        />
      ),
    },
    {
      id: `${key}Wow`,
      accessorFn: r => (r.wowComparable ? r[key]?.deltaPct ?? null : null),
      header: `${label} WoW`,
      filterFn: 'numberRange',
      meta: { label: `${label} WoW %`, filterVariant: 'numberRange' },
      cell: ({ row }) => (
        <DeltaBadge
          pct={row.original[key]?.deltaPct}
          comparable={row.original.wowComparable}
          reason={row.original.wowBlockedReason}
        />
      ),
    },
  ];
}

// ── Summary ──────────────────────────────────────────────────────────────────

function SummaryTab({ rows }) {
  const columns = useMemo(() => [
    {
      id: 'clientName',
      accessorFn: r => r.clientName ?? '',
      header: 'Client',
      filterFn: 'includesString',
      meta: { label: 'Client', filterVariant: 'text' },
      cell: ({ row }) => <ClientCell {...row.original} />,
    },
    {
      accessorKey: 'activationStatus',
      header: 'Status',
      filterFn: 'equalsString',
      meta: { label: 'Status', filterVariant: 'select', filterOptions: ACTIVATION_OPTIONS },
      cell: ({ row }) => <ActivationPill status={row.original.activationStatus} />,
    },
    {
      accessorKey: 'kamName',
      header: 'KAM',
      filterFn: 'equalsString',
      meta: { label: 'KAM', filterVariant: 'select' },
      cell: ({ row }) => (row.original.kamName
        ? <span className="client-chip">{row.original.kamName}</span>
        : <span className="contact-missing">—</span>),
    },
    {
      accessorKey: 'allocationStatus',
      header: 'Allocation',
      filterFn: 'equalsString',
      meta: { label: 'Allocation', filterVariant: 'select' },
      cell: ({ row }) => (
        <>
          <AllocationPill status={row.original.allocationStatus} />
          {row.original.unassignedExposureValue > 0 && (
            <div className="num-sub">{fmtInr(row.original.unassignedExposureValue)} unowned</div>
          )}
        </>
      ),
    },
    ...metricColumns('overdue', 'Overdue'),
    ...metricColumns('rejected', 'Rejected'),
    ...metricColumns('noGrn', 'No GRN'),
    ...metricColumns('shortGrn', 'Discrepancy'),
    {
      id: 'totalOpen',
      accessorFn: r => r.totals?.currentValue ?? 0,
      header: 'Total open',
      filterFn: 'numberRange',
      meta: { label: 'Total open', filterVariant: 'numberRange' },
      cell: ({ row }) => <MoneyCell value={row.original.totals?.currentValue} />,
    },
    {
      id: 'closedLastWeek',
      accessorFn: r => r.closedLastWeek ?? 0,
      header: 'Closed 7d',
      filterFn: 'numberRange',
      meta: { label: 'Closed 7d', filterVariant: 'numberRange' },
      cell: ({ row }) => <MoneyCell value={row.original.closedLastWeek} />,
    },
    {
      id: 'wowBasis',
      accessorFn: r => (r.wowComparable ? 'Like-for-like' : 'Not comparable'),
      header: 'WoW basis',
      filterFn: 'equalsString',
      meta: { label: 'WoW basis', filterVariant: 'select' },
      cell: ({ row }) => (row.original.wowComparable
        ? <span className="contact-ok">like-for-like</span>
        : (
          <span className="contact-missing" title={row.original.wowBlockedReason || ''}>
            {row.original.newPlatforms?.length ? 'new platform' : 'no baseline'}
          </span>
        )),
    },
  ], []);

  const totalOpen = rows.reduce((s, r) => s + (r.totals?.currentValue ?? 0), 0);
  const totalClosed = rows.reduce((s, r) => s + (r.closedLastWeek ?? 0), 0);
  const trialCount = rows.filter(r => r.activationStatus === 'TRIAL').length;
  const notComparable = rows.filter(r => !r.wowComparable).length;

  return (
    <div className="page-content">
      <div className="section-header">
        <h2>Client Metrics Summary</h2>
        <div className="header-right">
          <span className="pill pill-grey">{rows.length} clients</span>
          {trialCount > 0 && <span className="pill pill-amber">{trialCount} trial</span>}
          <span className="pill pill-grey">{fmtInr(totalOpen)} open</span>
          <span className="pill pill-green">{fmtInr(totalClosed)} closed last 7d</span>
        </div>
      </div>
      <div className="sheetwa-instructions">
        <span className="inst-icon">ℹ️</span>
        <span>
          Values are live ticket state over the platforms the brand can see, so they tally with the
          client&apos;s Actions pages.
          {notComparable > 0 && (
            <>
              {' '}<b>{notComparable}</b> client{notComparable !== 1 ? 's have' : ' has'} no
              like-for-like baseline this week — a platform joined since the comparison point, so the
              movement is withheld rather than reported as a jump.
            </>
          )}
        </span>
      </div>
      <DataTable columns={columns} data={rows} emptyMessage="No clients match the selected filters." />
    </div>
  );
}

// ── Allocation ───────────────────────────────────────────────────────────────

function AllocationTab({ clients }) {
  const rows = useMemo(() => (clients || []).map(c => ({
    clientId: c.clientId,
    clientName: c.clientName,
    activationStatus: c.activationStatus,
    kamName: c.kamName,
    status: c.allocation?.status ?? 'NONE',
    assigneeCount: c.allocation?.assigneeCount ?? 0,
    assignedIssueTypes: c.allocation?.assignedIssueTypes ?? [],
    unassignedIssueTypes: c.allocation?.unassignedIssueTypes ?? [],
    unassignedExposureValue: c.allocation?.unassignedExposureValue ?? 0,
    lastAssignedAt: c.allocation?.lastAssignedAt ?? null,
  })), [clients]);

  const columns = useMemo(() => [
    {
      id: 'clientName',
      accessorFn: r => r.clientName ?? '',
      header: 'Client',
      filterFn: 'includesString',
      meta: { label: 'Client', filterVariant: 'text' },
      cell: ({ row }) => <ClientCell {...row.original} kamName={null} />,
    },
    {
      accessorKey: 'activationStatus',
      header: 'Status',
      filterFn: 'equalsString',
      meta: { label: 'Status', filterVariant: 'select', filterOptions: ACTIVATION_OPTIONS },
      cell: ({ row }) => <ActivationPill status={row.original.activationStatus} />,
    },
    {
      accessorKey: 'kamName',
      header: 'KAM',
      filterFn: 'equalsString',
      meta: { label: 'KAM', filterVariant: 'select' },
      cell: ({ row }) => (row.original.kamName || <span className="contact-missing">—</span>),
    },
    {
      accessorKey: 'status',
      header: 'Allocation',
      filterFn: 'equalsString',
      meta: { label: 'Allocation', filterVariant: 'select' },
      cell: ({ row }) => <AllocationPill status={row.original.status} />,
    },
    {
      accessorKey: 'assigneeCount',
      header: 'Owners',
      filterFn: 'numberRange',
      meta: { label: 'Owners', filterVariant: 'numberRange' },
      cell: ({ row }) => (
        <div className="num-cell"><div className="num-primary">{row.original.assigneeCount}</div></div>
      ),
    },
    {
      id: 'assignedIssueTypes',
      accessorFn: r => r.assignedIssueTypes.map(t => ISSUE_TYPES[t]?.short ?? t).join(', '),
      header: 'Owned types',
      filterFn: 'includesString',
      meta: { label: 'Owned types', filterVariant: 'text' },
      cell: ({ row }) => (row.original.assignedIssueTypes.length
        ? row.original.assignedIssueTypes.map(t => (
          <span key={t} className="desig-chip">{ISSUE_TYPES[t]?.short ?? t}</span>
        ))
        : <span className="contact-missing">none</span>),
    },
    {
      id: 'unassignedIssueTypes',
      accessorFn: r => r.unassignedIssueTypes.map(t => ISSUE_TYPES[t]?.short ?? t).join(', '),
      header: 'Unowned types',
      filterFn: 'includesString',
      meta: { label: 'Unowned types', filterVariant: 'text' },
      cell: ({ row }) => (row.original.unassignedIssueTypes.length
        ? row.original.unassignedIssueTypes.map(t => (
          <span key={t} className="desig-chip">{ISSUE_TYPES[t]?.short ?? t}</span>
        ))
        : <span className="contact-ok">all covered</span>),
    },
    {
      accessorKey: 'unassignedExposureValue',
      header: 'Unowned value',
      filterFn: 'numberRange',
      meta: { label: 'Unowned value', filterVariant: 'numberRange' },
      cell: ({ row }) => <MoneyCell value={row.original.unassignedExposureValue} />,
    },
    {
      accessorKey: 'lastAssignedAt',
      header: 'Last assigned',
      filterFn: 'dateRange',
      meta: { label: 'Last assigned', filterVariant: 'dateRange' },
      cell: ({ row }) => (row.original.lastAssignedAt
        ? new Date(row.original.lastAssignedAt).toLocaleDateString('en-IN', {
          day: 'numeric', month: 'short', year: 'numeric',
        })
        : <span className="contact-missing">—</span>),
    },
  ], []);

  const notFull = rows.filter(r => r.status !== 'FULL');
  const unownedTotal = rows.reduce((s, r) => s + r.unassignedExposureValue, 0);

  return (
    <div className="page-content">
      <div className="section-header">
        <h2>Responsibility Allocation</h2>
        <div className="header-right">
          <span className="pill pill-amber">{notFull.length} need attention</span>
          <span className="pill pill-red">{fmtInr(unownedTotal)} unowned exposure</span>
        </div>
      </div>
      <div className="sheetwa-instructions">
        <span className="inst-icon">ℹ️</span>
        <span>
          A client is <b>Partly allocated</b> when some issue types — or some platforms within an
          issue type — have no owner. That unowned exposure is still real; nobody is accountable for it.
        </span>
      </div>
      <DataTable columns={columns} data={rows} emptyMessage="No clients match the selected filters." />
    </div>
  );
}

// ── Responsibles ─────────────────────────────────────────────────────────────

function ResponsiblesTab({ rows }) {
  const columns = useMemo(() => [
    {
      id: 'name',
      accessorFn: r => r.name ?? '',
      header: 'Person',
      filterFn: 'includesString',
      meta: { label: 'Person', filterVariant: 'text' },
      cell: ({ row }) => (
        <span className="client-name-cell">
          <span className="client-name">{row.original.name}</span>
          <span className="client-id">
            {row.original.contactNumber || <span className="contact-missing">no contact</span>}
            {row.original.designation ? ` · ${row.original.designation}` : ''}
          </span>
        </span>
      ),
    },
    {
      accessorKey: 'clientName',
      header: 'Client',
      filterFn: 'includesString',
      meta: { label: 'Client', filterVariant: 'text' },
      cell: ({ row }) => (
        <span className="client-chip">
          {row.original.clientName}
          {row.original.activationStatus === 'TRIAL' && (
            <span className="new-plat-tag trial-tag">TRIAL</span>
          )}
        </span>
      ),
    },
    {
      accessorKey: 'activationStatus',
      header: 'Status',
      filterFn: 'equalsString',
      meta: { label: 'Status', filterVariant: 'select', filterOptions: ACTIVATION_OPTIONS },
      cell: ({ row }) => <ActivationPill status={row.original.activationStatus} />,
    },
    {
      accessorKey: 'kamName',
      header: 'KAM',
      filterFn: 'equalsString',
      meta: { label: 'KAM', filterVariant: 'select' },
      cell: ({ row }) => (row.original.kamName || <span className="contact-missing">—</span>),
    },
    {
      accessorKey: 'issueLabel',
      header: 'Issue',
      filterFn: 'equalsString',
      meta: { label: 'Issue', filterVariant: 'select' },
      cell: ({ row }) => <span className="desig-chip">{row.original.issueLabel}</span>,
    },
    {
      id: 'platforms',
      accessorFn: r => (r.platforms?.length ? r.platforms.join(', ') : 'All'),
      header: 'Platforms',
      filterFn: 'includesString',
      meta: { label: 'Platforms', filterVariant: 'text' },
    },
    {
      accessorKey: 'status',
      header: 'Severity',
      filterFn: 'equalsString',
      meta: { label: 'Severity', filterVariant: 'select' },
      cell: ({ row }) => (row.original.status === 'severe'
        ? <span className="pill pill-red">severe</span>
        : <span className="pill pill-grey">{row.original.status}</span>),
    },
    {
      accessorKey: 'currentValue',
      header: 'Owns',
      filterFn: 'numberRange',
      meta: { label: 'Owns', filterVariant: 'numberRange' },
      cell: ({ row }) => <MoneyCell value={row.original.currentValue} />,
    },
    {
      id: 'deltaPct',
      accessorFn: r => (r.wowComparable ? r.deltaPct ?? null : null),
      header: 'WoW',
      filterFn: 'numberRange',
      meta: { label: 'WoW %', filterVariant: 'numberRange' },
      cell: ({ row }) => (
        <DeltaBadge
          pct={row.original.deltaPct}
          comparable={row.original.wowComparable}
          reason={row.original.wowBlockedReason}
        />
      ),
    },
    {
      accessorKey: 'pendingValue',
      header: 'No action yet',
      filterFn: 'numberRange',
      meta: { label: 'No action yet', filterVariant: 'numberRange' },
      cell: ({ row }) => <MoneyCell value={row.original.pendingValue} />,
    },
    {
      accessorKey: 'disputeValue',
      header: 'Disputed',
      filterFn: 'numberRange',
      meta: { label: 'Disputed', filterVariant: 'numberRange' },
      cell: ({ row }) => <MoneyCell value={row.original.disputeValue} />,
    },
    {
      accessorKey: 'old90Value',
      header: '90+ days',
      filterFn: 'numberRange',
      meta: { label: '90+ days', filterVariant: 'numberRange' },
      cell: ({ row }) => <MoneyCell value={row.original.old90Value} />,
    },
    {
      accessorKey: 'closedLastWeek',
      header: 'Closed 7d',
      filterFn: 'numberRange',
      meta: { label: 'Closed 7d', filterVariant: 'numberRange' },
      cell: ({ row }) => <MoneyCell value={row.original.closedLastWeek} />,
    },
    {
      accessorKey: 'actionAgeingDays',
      header: 'Last action',
      filterFn: 'numberRange',
      meta: { label: 'Days since action', filterVariant: 'numberRange' },
      cell: ({ row }) => {
        const days = row.original.actionAgeingDays;
        if (days === null || days === undefined) return <span className="contact-missing">never</span>;
        return <span className={days > 7 ? 'contact-missing' : 'contact-ok'}>{days}d ago</span>;
      },
    },
    {
      accessorKey: 'ageingBucket',
      header: 'Oldest',
      filterFn: 'equalsString',
      meta: { label: 'Oldest bucket', filterVariant: 'select' },
      cell: ({ row }) => (row.original.ageingBucket || <span className="contact-missing">—</span>),
    },
  ], []);

  const severeCount = rows.filter(r => r.status === 'severe').length;
  const noContact = rows.filter(r => !r.contactNumber).length;

  return (
    <div className="page-content">
      <div className="section-header">
        <h2>Responsible People</h2>
        <div className="header-right">
          <span className="pill pill-red">{severeCount} severe</span>
          <span className="pill pill-grey">{noContact} missing contact</span>
        </div>
      </div>
      <div className="sheetwa-instructions">
        <span className="inst-icon">ℹ️</span>
        <span>
          One row per person per issue type. Where two people share an assignment both see the full
          amount, so these rows <b>do not sum</b> to the client total — use the Summary tab for totals.
        </span>
      </div>
      <DataTable columns={columns} data={rows} emptyMessage="No owners match the selected filters." />
    </div>
  );
}

// ── Campaigns ────────────────────────────────────────────────────────────────

// Finance Exec and Ops Exec are retired. Each remaining campaign goes to every
// product user at the client — the designation-scoped variants are gone, so
// there is no longer an "All User Types" second download beside each card.
const CAMPAIGNS = [
  {
    key: 'founderSummary',
    label: 'Founder Summary',
    icon: '📋',
    desc: 'Every client · all product users',
    writer: exportFounderSummaryExcel,
  },
  {
    key: 'founderWoW',
    label: 'Founder WoW',
    icon: '🎯',
    desc: 'All product users · only clients with a like-for-like baseline',
    writer: exportFounderWoWExcel,
  },
  {
    key: 'responsibles',
    label: 'Responsibility Message',
    icon: '👤',
    desc: 'One message per allocated owner · contact number and movement',
    writer: exportResponsibleExcel,
  },
];

// No message body and no per-KAM send, so this keeps the generic writer.
const LIVE_CAMPAIGNS = [
  { key: 'allocationChase', label: 'Allocation Chase', icon: '⚠️', desc: 'Clients with unowned exposure — no message body' },
];

function CampaignsTab({ campaigns, kamFilter, kamNames, activationFilter }) {
  const [downloading, setDownloading] = useState(null);

  // Every export goes through the activation scope, so a trial brand can never
  // ride along in an active-client send. The file name records the scope too,
  // because the sheet leaves this app and the distinction has to survive it.
  const scope = rows => (rows || []).filter(r => matchesActivation(r.activation_status, activationFilter));
  const scopeSuffix = activationFilter.length === 1 ? `_${activationFilter[0]}` : '';

  // Mirrors the legacy handler: a single KAM name narrows the export (and names
  // the sheet), 'All' or a multi-select exports everything visible.
  const handleDownload = (campaignKey, campaignLabel, kam, writer) => {
    const key = `${campaignKey}_${Array.isArray(kam) ? kam.join('|') : kam}`;
    setDownloading(key);
    setTimeout(() => {
      const sourceRows = scope(campaigns[campaignKey]);
      const rows = Array.isArray(kam) ? sourceRows.filter(r => kam.includes(r.kam_name)) : sourceRows;
      const kamArg = kam === 'All' || Array.isArray(kam) ? null : kam;
      const safeName = `${campaignLabel.replace(/\s+/g, '_')}${scopeSuffix}`;
      writer(rows, safeName, kamArg);
      setDownloading(null);
    }, 300);
  };

  const handleGeneric = (key, label, fn) => {
    setDownloading(key);
    setTimeout(() => {
      const rows = filterCampaignRows(campaigns[key], kamFilter, activationFilter);
      fn(rows, `${label.replace(/\s+/g, '_')}${scopeSuffix}`, null);
      setDownloading(null);
    }, 300);
  };

  return (
    <div className="page-content">
      <div className="section-header">
        <h2>Campaign Downloads</h2>
        <span className="row-count">SheetWA-ready Excel files · live data</span>
      </div>

      <div className="sheetwa-instructions">
        <span className="inst-icon">ℹ️</span>
        <span>Upload to SheetWA → set contact column = <code>contact_number</code> → Write Message → type <code>@Message</code> → Send</span>
      </div>

      <div className="campaigns-grid">
        {CAMPAIGNS.map(camp => {
          const rows = campaigns[camp.key] || [];
          const allKamsSelected = kamFilter.length === 0;
          const displayRows = allKamsSelected ? rows : rows.filter(r => kamFilter.includes(r.kam_name));
          const rowKamNames = [...new Set(rows.map(r => r.kam_name).filter(Boolean))];
          const visibleKamNames = (kamNames || rowKamNames).filter(k => rowKamNames.includes(k));
          const kamsAvailable = ['All', ...(kamNames || visibleKamNames)];

          return (
            <div key={camp.key} className="campaign-card">
              <div className="campaign-card-header">
                <span className="camp-icon">{camp.icon}</span>
                <div>
                  <h3>{camp.label}</h3>
                  <p className="camp-desc">{camp.desc}</p>
                </div>
                <span className="camp-total">{displayRows.length} users</span>
              </div>

              <div className="camp-stats">
                {(allKamsSelected ? visibleKamNames : visibleKamNames.filter(k => kamFilter.includes(k))).map(k => {
                  const count = rows.filter(r => r.kam_name === k).length;
                  const withContact = rows.filter(r => r.kam_name === k && r.contact_number).length;
                  return (
                    <div key={k} className="camp-stat">
                      <span className="kam-chip">{k}</span>
                      <span className="stat-num">{count}</span>
                      <span className="stat-contact">{withContact} contacts</span>
                    </div>
                  );
                })}
              </div>

              <div className="download-buttons">
                {(allKamsSelected ? kamsAvailable : kamFilter).map(kam => {
                  const count = kam === 'All' ? rows.length : rows.filter(r => r.kam_name === kam).length;
                  const key = `${camp.key}_${kam}`;
                  const isLoading = downloading === key;
                  return (
                    <button key={kam} className={`dl-btn ${kam === 'All' ? 'dl-all' : 'dl-kam'}`}
                            onClick={() => handleDownload(camp.key, camp.label, kam, camp.writer)}
                            disabled={isLoading || count === 0}>
                      {isLoading ? '⏳' : '⬇'} {kam === 'All' ? 'All KAMs' : kam}
                      <span className="dl-count">{count}</span>
                    </button>
                  );
                })}
              </div>

            </div>
          );
        })}
      </div>

      <div className="section-header">
        <h2>Live-only exports</h2>
        <span className="row-count">Not available from the funnel snapshot</span>
      </div>
      <div className="campaigns-grid">
        {LIVE_CAMPAIGNS.map(camp => {
          const rows = campaigns[camp.key] || [];
          const visible = kamFilter.length ? rows.filter(r => kamFilter.includes(r.kam_name)) : rows;
          const withContact = visible.filter(r => r.contact_number).length;
          return (
            <div key={camp.key} className="campaign-card">
              <div className="campaign-card-header">
                <span className="camp-icon">{camp.icon}</span>
                <div>
                  <h3>{camp.label}</h3>
                  <p className="camp-desc">{camp.desc}</p>
                </div>
                <span className="camp-total">{visible.length} rows</span>
              </div>
              <div className="camp-stats">
                <div className="camp-stat">
                  <span className="stat-num">{withContact}</span>
                  <span className="stat-contact">with contact</span>
                </div>
                <div className="camp-stat">
                  <span className="stat-num">{visible.length - withContact}</span>
                  <span className="stat-contact">missing</span>
                </div>
              </div>
              <div className="download-buttons">
                <Button variant="outline" size="sm"
                        disabled={downloading === camp.key || visible.length === 0}
                        onClick={() => handleGeneric(camp.key, camp.label, exportGenericExcel)}>
                  <Download size={14} /> Excel
                </Button>
                <Button variant="ghost" size="sm"
                        disabled={downloading === camp.key || visible.length === 0}
                        onClick={() => handleGeneric(camp.key, camp.label, exportCustomerIoJson)}>
                  <Mail size={14} /> Customer.io JSON
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'summary',      label: 'Summary',      icon: BarChart3 },
  { key: 'allocation',   label: 'Allocation',   icon: Users },
  { key: 'responsibles', label: 'Responsibles', icon: Users },
  { key: 'campaigns',    label: 'Campaigns',    icon: Download },
];

export default function CampaignsLiveDashboard() {
  const { currentUser } = useAuth();
  const jwtCfg = useApiConfig();

  // Prefer the signed-in user's JWT: only it carries an identity, so only it
  // gets per-KAM scoping. The direct access code is the fallback and is
  // anonymous, so the backend treats it as ADMIN and returns every active
  // client. Never send both — the backend checks the code first and would
  // discard the JWT's scoping.
  const cfg = useMemo(
    () => jwtCfg ?? (BACKEND_DIRECT_ACCESS_CODE ? { directAccessCode: BACKEND_DIRECT_ACCESS_CODE } : null),
    [jwtCfg],
  );
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [kamFilter, setKamFilter] = useState(
    () => (currentUser?.kamFullName ? [currentUser.kamFullName] : []),
  );
  // Trial brands ship in the payload but are not part of a normal send, so the
  // page opens scoped to ACTIVE. Switching to Trial is a deliberate act.
  const [activationFilter, setActivationFilter] = useState(['ACTIVE']);

  const load = useCallback(async (refresh = false, signal) => {
    if (!cfg) return;
    setLoading(true);
    setError(null);
    try {
      setData(await fetchCampaignLiveMetrics(BACKEND_BASE_URL, cfg, { refresh, signal }));
    } catch (e) {
      if (e.name === 'AbortError') return;
      setError(e.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [cfg]);

  // AbortController (not just a stale-response guard) so React 18 StrictMode's
  // dev-only double-invoke of this effect — mount, cleanup, mount again —
  // cancels the first in-flight request instead of leaving two live ones.
  useEffect(() => {
    const controller = new AbortController();
    load(false, controller.signal);
    return () => controller.abort();
  }, [load]);

  const clients = useMemo(() => (data?.clients ?? []).filter(c =>
    matchesKam(c.kamName, kamFilter)
    && matchesActivation(c.activationStatus, activationFilter)),
  [data, kamFilter, activationFilter]);

  const activationCounts = useMemo(() => {
    const counts = { ACTIVE: 0, TRIAL: 0 };
    (data?.clients ?? []).forEach(c => {
      if (counts[c.activationStatus] !== undefined) counts[c.activationStatus] += 1;
    });
    return counts;
  }, [data]);

  const kamNames = useMemo(() => buildKamNames(data?.clients), [data]);
  const summaryRows = useMemo(() => buildSummaryRows(clients), [clients]);
  const responsibleRows = useMemo(() => buildResponsibleRows(clients), [clients]);
  const campaigns = useMemo(() => buildLiveCampaigns(clients), [clients]);

  const toggleKam = name => setKamFilter(sel => (
    name === 'All' ? [] : sel.includes(name) ? sel.filter(k => k !== name) : [...sel, name]
  ));

  const toggleActivation = status => setActivationFilter(sel => (
    status === 'All' ? [] : sel.includes(status) ? sel.filter(a => a !== status) : [...sel, status]
  ));

  if (!cfg) {
    return (
      <div className="page-content">
        <div className="connect-form drop-error">
          <h2>No credentials available</h2>
          <p className="error-msg">
            Sign in with your internal-user account, or set
            REACT_APP_BACKEND_DIRECT_ACCESS_CODE so the dashboard can fall back to machine access.
          </p>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="page-content">
        <div className="connect-form">
          <div className="loading-state">
            <div className="spinner" />
            <p>Computing live metrics from open tickets…</p>
            <span className="camp-desc">
              This reads live ticket state per client, so the first load can take a while.
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="page-content">
        <div className="connect-form drop-error">
          <h2>Couldn't load campaign metrics</h2>
          <p className="error-msg">{error}</p>
          <Button variant="outline" size="sm" onClick={() => load(false)}>Retry</Button>
        </div>
      </div>
    );
  }

  const failedCount = (data?.clients ?? []).filter(c => c.error).length;

  return (
    <>
      <div className="campaign-page-header">
        <div>
          <span className="eyebrow">Campaigns · Live</span>
          <h1>Campaign performance</h1>
          <p>
            Live open exposure from action tickets — the same numbers as the action-page KPI cards.
            Week-on-week compares against the prior-week funnel snapshot.
          </p>
        </div>
        <Button variant="outline" size="sm" disabled={loading} onClick={() => load(true)}>
          <RotateCcw size={15} />{loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      <div className="data-meta">
        <div className="meta-item">
          <span className="meta-label">Generated</span>
          <span className="meta-val">
            {data?.generatedAt ? new Date(data.generatedAt).toLocaleString('en-IN') : '—'}
          </span>
        </div>
        <div className="meta-item">
          <span className="meta-label">Scope</span>
          <span className="meta-val">
            {data?.actor?.role === 'ADMIN' ? 'All active clients' : 'Your clients'} · {clients.length}
          </span>
        </div>
        {failedCount > 0 && (
          <div className="meta-fallback-notice">
            {failedCount} client{failedCount !== 1 ? 's' : ''} failed to compute — shown with zeroed metrics.
          </div>
        )}
      </div>

      <div className="campaign-tabs" role="tablist" aria-label="Campaign views">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <Button
              key={t.key} role="tab" size="sm"
              aria-selected={activeTab === t.key}
              variant={activeTab === t.key ? 'secondary' : 'ghost'}
              onClick={() => setActiveTab(t.key)}
            >
              <Icon size={15} />{t.label}
            </Button>
          );
        })}
      </div>

      {kamNames.length > 0 && (
        <header className="topbar">
          <div className="kam-filter">
            {['All', ...kamNames].map(k => {
              const active = k === 'All' ? kamFilter.length === 0 : kamFilter.includes(k);
              return (
                <button
                  key={k} type="button" aria-pressed={active}
                  className={`kam-btn ${active ? 'active' : ''}`}
                  onClick={() => toggleKam(k)}
                >
                  {k}
                </button>
              );
            })}
          </div>
          <span className="topbar-hint">Filter by KAM across all views</span>
        </header>
      )}

      <header className="topbar">
        <div className="kam-filter">
          {['All', 'ACTIVE', 'TRIAL'].map(status => {
            const active = status === 'All'
              ? activationFilter.length === 0
              : activationFilter.includes(status);
            const count = status === 'All'
              ? activationCounts.ACTIVE + activationCounts.TRIAL
              : activationCounts[status];
            return (
              <button
                key={status} type="button" aria-pressed={active}
                className={`kam-btn ${active ? 'active' : ''}`}
                onClick={() => toggleActivation(status)}
              >
                {status === 'All' ? 'All' : ACTIVATION_LABEL[status].label} ({count})
              </button>
            );
          })}
        </div>
        <span className="topbar-hint">
          Trial brands are excluded by default and export to their own file
        </span>
      </header>

      {activeTab === 'summary'      && <SummaryTab rows={summaryRows} />}
      {activeTab === 'allocation'   && <AllocationTab clients={clients} />}
      {activeTab === 'responsibles' && <ResponsiblesTab rows={responsibleRows} />}
      {activeTab === 'campaigns'    && (
        <CampaignsTab
          campaigns={campaigns}
          kamFilter={kamFilter}
          kamNames={kamNames}
          activationFilter={activationFilter}
        />
      )}
    </>
  );
}

export { ISSUE_ORDER };
